import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { getTimeStampForLeads } from "../utils/moment";
import { contactAdapter } from "../utils/contacts";
import { Payment } from "../interfaces";
/** Estado del store */
interface PaymentsState {
  payments: Payment[];
  setPayments: (payments: Payment[]) => void;
  getPayment: (id: string) => Payment | undefined;
  updatePaymentReconciled: (params: {
    id: string;
    reconciled: boolean;
    reconciledAt: string | null;
    reconciledBy: string | null;
  }) => void;
}

function formatCurrency(amount: number, currency: string): string {
  if (amount == null || !currency) return "";
  const formattedAmount = (amount / 100).toFixed(2);
  return (
    Intl.NumberFormat("en-US").format(Number(formattedAmount)) +
    " " +
    currency.toUpperCase()
  );
}

function calculateExchangeRate(exchangeRate: number): string {
  if (!exchangeRate) return "";
  const er = (1 / exchangeRate).toFixed(4);
  return er;
}

function formatPropertyValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "number") return String(value);
  return String(value).trim();
}

/** Store sin persistencia */
export const usePaymentsStore = create<PaymentsState>()(
  subscribeWithSelector(
    devtools((set, get) => ({
      payments: [],
      setPayments: (payments: any) =>
        set((state) => ({
          ...state,
          payments: payments.map((p: any) => {
            return {
              id: p._id,
              amountRaw: p.amount,
              amount: formatCurrency(p.amount, p.currency),
              totalAmount: formatCurrency(p.totalAmount, p.currency),
              percentage: (p.percentage * 100).toFixed(2) + "%", // round
              date: getTimeStampForLeads(p.createdAt),
              company: p.companies?.[0]?.name || "",
              paymentStatus: p.paid ? "paid" : p.active ? "active" : "expired",
              transferStatus: p.transfered ? "transfered" : "pendingTransfer",
              refundStatus: p.refunded ? "refunded" : "pendingRefund",
              convertedAmount: formatCurrency(
                p.balance?.amount,
                p.balance?.currency,
              ),
              exchangeRate: calculateExchangeRate(p.balance?.exchangeRate),
              stripeFee: formatCurrency(p.balance?.fee, p.balance?.currency),
              netAmount: formatCurrency(p.balance?.net, p.balance?.currency),
              commissionRate:
                ((p.balance?.commissionRate || 0) * 100).toFixed(2) + "%",
              platformCommission: formatCurrency(
                p.balance?.platformCommission,
                p.balance?.currency,
              ),
              providerNet: formatCurrency(
                p.balance?.providerNet,
                p.balance?.currency,
              ),
              contact: contactAdapter(p.contacts?.[0]),
              currency: p.currency?.toUpperCase?.() || "",
              provider: p.provider || null,
              itemName: p.item?.name || "",
              itemQuantity:
                Number.isFinite(Number(p.item?.quantity)) &&
                Number(p.item?.quantity) > 0
                  ? Number(p.item?.quantity)
                  : 1,
              itemPropertyValues: (p.item?.propertyValues || [])
                .map((property: any) => ({
                  fieldId: property?.fieldId || "",
                  label: property?.label || "",
                  type: property?.type || "text",
                  value: property?.value,
                }))
                .filter(
                  (property: any) =>
                    property.fieldId && property.label && property.value != null,
                ),
              itemReferenceHint: (() => {
                const firstProperty = (p.item?.propertyValues || []).find(
                  (property: any) =>
                    property?.label &&
                    property?.value !== undefined &&
                    property?.value !== null &&
                    String(property.value).trim() !== "",
                );
                if (!firstProperty) return "";
                return `${firstProperty.label}: ${formatPropertyValue(
                  firstProperty.value,
                )}`;
              })(),
              source: p.source || "booking",
              reconciled: Boolean(p.reconciled),
              reconciledAt: p.reconciledAt || null,
              reconciledBy: p.reconciledBy || null,
              bookings: (p.bookings || []).map((b: any) => ({
                externalId: b.externalId,
                amountPaid: formatCurrency(b.amountPaid, p.currency),
                grandTotal: formatCurrency(b.grandTotal, p.currency),
              })),
              refunds: (p.balance?.refunds || []).map((r: any) => ({
                amount: formatCurrency(r.amount, p.currency),
                date: getTimeStampForLeads(r.createdAt),
                status: r.status,
              })),
            };
          }),
        })),
      getPayment: (id) => get().payments.find((p) => p.id === id),
      updatePaymentReconciled: ({
        id,
        reconciled,
        reconciledAt,
        reconciledBy,
      }) =>
        set((state) => ({
          ...state,
          payments: state.payments.map((p) =>
            p.id === id
              ? {
                  ...p,
                  reconciled,
                  reconciledAt,
                  reconciledBy,
                }
              : p,
          ),
        })),
    })),
  ),
);
